import React from 'react';
import './ScanCardSkeleton.css';

/**
 * Skeleton loader for scan card
 * 
 * Matches the exact dimensions of the real ScanCard component
 * to prevent Cumulative Layout Shift (CLS).
 * 
 * Features:
 * - Shimmer animation
 * - Exact dimension matching
 * - Responsive design
 * - Accessible (aria-busy, aria-label)
 */
export const ScanCardSkeleton = () => {
  return (
    <div 
      className="scan-card-skeleton" 
      role="status" 
      aria-busy="true" 
      aria-label="Loading scan"
      data-testid="scan-card-skeleton"
    >
      <div className="skeleton-header">
        <div className="skeleton-title-group">
          <div className="skeleton-title shimmer" />
          <div className="skeleton-subtitle shimmer" />
        </div>
        <div className="skeleton-badge shimmer" />
      </div>
      
      <div className="skeleton-body">
        <div className="skeleton-text shimmer" />
        <div className="skeleton-text shimmer" style={{ width: '80%' }} />
        
        <div className="skeleton-stats">
          <div className="skeleton-stat shimmer">
            <div className="skeleton-stat-value" />
            <div className="skeleton-stat-label" />
          </div>
          <div className="skeleton-stat shimmer">
            <div className="skeleton-stat-value" />
            <div className="skeleton-stat-label" />
          </div>
          <div className="skeleton-stat shimmer">
            <div className="skeleton-stat-value" />
            <div className="skeleton-stat-label" />
          </div>
          <div className="skeleton-stat shimmer">
            <div className="skeleton-stat-value" />
            <div className="skeleton-stat-label" />
          </div>
        </div>
      </div>
      
      <div className="skeleton-footer">
        <div className="skeleton-timestamp shimmer" />
        <div className="skeleton-button shimmer" />
      </div>
      
      <span className="sr-only">Loading scan information...</span>
    </div>
  );
};

/**
 * Skeleton loader for scan list
 * 
 * Renders multiple scan card skeletons for initial page load.
 * 
 * @param {Object} props
 * @param {number} [props.count=6] - Number of skeleton cards to render
 */
export const ScanListSkeleton = ({ count = 6 }) => {
  return (
    <div className="scan-list-skeleton" data-testid="scan-list-skeleton">
      {Array.from({ length: count }).map((_, index) => (
        <ScanCardSkeleton key={`skeleton-${index}`} />
      ))}
    </div>
  );
};

/**
 * Compact skeleton for pagination loading
 * 
 * Used at the bottom of the list during infinite scroll.
 * Smaller and less prominent than full skeleton.
 */
export const ScanCardSkeletonCompact = () => {
  return (
    <div 
      className="scan-card-skeleton scan-card-skeleton--compact" 
      role="status" 
      aria-busy="true"
      aria-label="Loading more scans"
    >
      <div className="skeleton-header">
        <div className="skeleton-title shimmer" />
        <div className="skeleton-badge shimmer" />
      </div>
      
      <div className="skeleton-body">
        <div className="skeleton-stats">
          <div className="skeleton-stat shimmer" />
          <div className="skeleton-stat shimmer" />
          <div className="skeleton-stat shimmer" />
          <div className="skeleton-stat shimmer" />
        </div>
      </div>
      
      <span className="sr-only">Loading more scans...</span>
    </div>
  );
};

export default ScanCardSkeleton;

// Made with Bob
